import React from 'react';
import dayjs from 'dayjs';
import { Sun, Moon, CheckCircle, XCircle, Hourglass } from 'lucide-react';
import { AttendanceEntry, AttendanceStatus, ShiftType, ExtraMachineRequest } from '../../services/attendanceApi';

// ── Shared types ─────────────────────────────────────────────────────────────

export interface EmployeeOption {
  id: number;
  designation?: string | null;
  canApproveAttendance?: boolean;
  type?: 'FIXED' | 'METER';
  rate?: number | null;
  shiftARate?: number | null;
  shiftBRate?: number | null;
  extraUnit?: 'MACHINE' | 'HOUR';
  extraRate?: number | null;
  user?: {
    id?: number;
    name?: string;
    email?: string;
    phone?: string | null;
    role?: string;
    roleId?: number | null;
    userRole?: { name?: string };
  };
}

export interface RoleOption {
  id: number;
  name: string;
  description?: string;
}

// Independent per-module capabilities: read↔canView, create↔canCreate, edit↔canUpdate, delete↔canDelete.
// All false (or the module absent) = no access (Off). Create without Read = data-entry only (add, no list).
export interface ModulePerm { read: boolean; create: boolean; edit: boolean; delete: boolean }
export const EMPTY_PERM: ModulePerm = { read: false, create: false, edit: false, delete: false };
export const anyPerm = (p?: ModulePerm): boolean => !!p && (p.read || p.create || p.edit || p.delete);

export interface CreateEmployeeForm {
  name: string;
  email: string;
  phone: string;
  password: string;
  designation: string;
  roleId: string;
  canApproveAttendance: boolean;
  payType: 'FIXED' | 'METER';
  rate: string;            // FIXED: monthly ₹; METER: default ₹/meter
  shiftARate: string;      // METER only
  shiftBRate: string;      // METER only
  extraUnit: 'MACHINE' | 'HOUR';
  extraRate: string;
  // module id → capabilities. Absent or all-false = no access (Off).
  modulePerms: Record<number, ModulePerm>;
}

export const emptyCreateForm: CreateEmployeeForm = {
  name: '', email: '', phone: '', password: '', designation: '', roleId: '', canApproveAttendance: false,
  payType: 'FIXED', rate: '', shiftARate: '', shiftBRate: '', extraUnit: 'MACHINE', extraRate: '', modulePerms: {},
};

// An employee counts as "salary set up" once a pay rate exists:
// FIXED → monthly base > 0; METER → any per-meter rate (shift A/B or default) > 0.
export const hasSalarySetup = (e: EmployeeOption): boolean =>
  e.type === 'METER'
    ? (e.shiftARate || 0) > 0 || (e.shiftBRate || 0) > 0 || (e.rate || 0) > 0
    : (e.rate || 0) > 0;

// ── Shared formatters ────────────────────────────────────────────────────────

export const fmt = (v?: string | null) => (v ? dayjs(v).format('DD MMM YYYY, hh:mm A') : '—');
export const fmtTime = (v?: string | null) => (v ? dayjs(v).format('hh:mm A') : '—');
export const nameOf = (e: AttendanceEntry) => e.employee?.user?.name || `Employee #${e.employeeId}`;
export const emName = (r: ExtraMachineRequest) => r.employee?.user?.name || `Employee #${r.employeeId}`;
export const rupee = (n?: number) => `₹${(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
export const initialOf = (name?: string | null) => (name || '?').trim().charAt(0).toUpperCase() || '?';

// Derive a "this clock-in doesn't match its shift" exception purely from the timestamp:
// a DAY entry clocked in during night hours (or vice-versa) is worth a second look before approving.
// Returns a human-readable note, or null when nothing looks off.
export const shiftTimeException = (e: AttendanceEntry): string | null => {
  const t = e.clockInAt ? dayjs(e.clockInAt) : null;
  if (!t) return null;
  const hour = t.hour();
  const looksNight = hour >= 18 || hour < 6;
  if (e.shiftType === 'DAY' && looksNight) {
    return `Marked Day, but clocked in at ${t.format('hh:mm A')} — looks like a night shift`;
  }
  if (e.shiftType === 'NIGHT' && !looksNight) {
    return `Marked Night, but clocked in at ${t.format('hh:mm A')} — looks like a day shift`;
  }
  return null;
};

// ── Shared presentational bits ───────────────────────────────────────────────

export const StatusBadge: React.FC<{ status: AttendanceStatus }> = ({ status }) => {
  const map: Record<AttendanceStatus, { cls: string; label: string; icon: React.ReactNode }> = {
    PENDING: { cls: 'bg-amber-100 text-amber-800', label: 'Pending', icon: <Hourglass className="h-3.5 w-3.5" /> },
    APPROVED: { cls: 'bg-green-100 text-green-800', label: 'Approved', icon: <CheckCircle className="h-3.5 w-3.5" /> },
    REJECTED: { cls: 'bg-red-100 text-red-800', label: 'Rejected', icon: <XCircle className="h-3.5 w-3.5" /> },
  };
  const s = map[status];
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${s.cls}`}>
      {s.icon}{s.label}
    </span>
  );
};

export const ShiftLabel: React.FC<{ shift: ShiftType }> = ({ shift }) => (
  <span className="inline-flex items-center gap-1 text-sm text-gray-700">
    {shift === 'DAY' ? <Sun className="h-4 w-4 text-amber-500" /> : <Moon className="h-4 w-4 text-primary-500" />}
    {shift === 'DAY' ? 'Day' : 'Night'}
  </span>
);

// Small round monogram used in the person rows.
export const Avatar: React.FC<{ name?: string | null; className?: string }> = ({ name, className = '' }) => (
  <div className={`h-7 w-7 shrink-0 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center text-xs font-bold text-gray-500 ${className}`}>
    {initialOf(name)}
  </div>
);
